import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { LOAD_MASTERS } from '@common/master/master';
import {
  CheckinSearchResultStoreService,
  FlightAvailabilityToCheckinStoreService,
  ServicingMybookingTransitionService,
} from '@common/services';
import { CheckinSearchService } from '@common/services/checkin-search/checkin-search.service';
import { environment } from '@env/environment';
import { AswMasterService, CommonLibService } from '@lib/services';
import { Observable, firstValueFrom, lastValueFrom, pipe, take, BehaviorSubject } from 'rxjs';
import { PostJourneysSearchJourneyRequest } from 'src/sdk_checkin';

@Injectable({
  providedIn: 'root',
})
export class CheckinSearchGuard implements CanActivate {
  private searchFormParamForUpdate$ = new BehaviorSubject<PostJourneysSearchJourneyRequest | undefined>(undefined);

  constructor(
    private router: Router,
    private commonService: CommonLibService,
    private checkinSearchService: CheckinSearchService,
    private checkinSearchResultStoreService: CheckinSearchResultStoreService,
    private aswMasterService: AswMasterService,
    private servicingMybookingTransitionService: ServicingMybookingTransitionService,
    private flightAvailabilityToCheckinStoreService: FlightAvailabilityToCheckinStoreService
  ) {
    // チェックイン検索処理にて必要なマスターの取得
    this.aswMasterService
      .load(
        [
          LOAD_MASTERS.FUNCTION_INELIGIBLE_REASON,
          LOAD_MASTERS.OFFICE_BY_OFFICE_CODE,
          LOAD_MASTERS.AIRLINE_I18NJOIN_BY_AIRLINE_CODE,
        ],
        true
      )
      .pipe(take(1))
      .subscribe();
  }

  /**
   * チェックイン検索画面表示前の処理
   * @returns
   */
  async checkinSearchGuard() {
    // TOPからの遷移の場合の処理を行う
    const CONNECTION_KIND = this.checkinSearchService.getConnectionKind();
    if (CONNECTION_KIND !== undefined && CONNECTION_KIND !== 'ZZZ') {
      // orderまたeTicketの必須パラメータが存在する場合
      const param = await this.checkinSearchService.getQueryOrPostParameterForAswTop();
      this.updateSearchFormParamForUpdate(param);
      if (param) {
        return await this.runCommonProcess(param, true);
      }
    }

    // 予約詳細からの遷移の場合の処理を行う
    else if (this.checkinSearchService.isInflowFromReservation()) {
      const param = this.checkinSearchService.getPostParameterForReservation();
      this.updateSearchFormParamForUpdate(param);
      // パラメータを取得してもundefinedの場合は外部流入は行われていないので、チェックイン検索画面に通す
      if (!!param) {
        // エラー監視及びエラー時の処理
        const subscription = this.checkinSearchResultStoreService.getErrorMessageId$().subscribe((id) => {
          const form = document.createElement('form');
          form.action = this.servicingMybookingTransitionService.getServicingPathWithConnectionKind();
          form.method = 'POST';
          document.body.append(form);
          form.addEventListener('formdata', (event) => {
            const formData = event.formData;
            formData.set('searchType', param.searchType);
            if (param.searchType === PostJourneysSearchJourneyRequest.SearchTypeEnum.Order) {
              formData.set('orderId', param.orderId!);
            }
            if (param.searchType === PostJourneysSearchJourneyRequest.SearchTypeEnum.ETicket) {
              formData.set('eTicketNumber', param.eTicketNumber!);
            }
            formData.set('firstName', param.firstName);
            formData.set('lastName', param.lastName);
            formData.set('SITE_ID', 'ASW_APP');
            if (!!id) {
              formData.set('errorId', id);
            }
          });
          this.checkinSearchService.clearCheckinSearchStore();
          form.submit();
        });

        const result = await this.runCommonProcess(param, false);
        subscription.unsubscribe();
        return result;
      }
    }
    // 空席照会結果(国内)からの遷移の場合の処理を行う
    else if (this.checkinSearchService.isInflowFromFlightAvailability()) {
      const param = this.checkinSearchService.getPostParameterForFlightAvailability();
      if (!!param) {
        // flightAvailabilityToCheckin(空席照会結果からの遷移情報)のstoreをクリアする
        this.flightAvailabilityToCheckinStoreService.resetFlightAvailabilityToCheckin();
        // flightAvailabilityToCheckin(空席照会結果からの遷移情報)のstoreにパラメータを設定する
        const flightAvailabilityParam = this.checkinSearchService.getFlightAvailabilityParameter();
        if (!!flightAvailabilityParam) {
          this.flightAvailabilityToCheckinStoreService.updateFlightAvailabilityToCheckinStatus({
            CONNECTION_KIND: 'ZZZ',
            outBoundId: flightAvailabilityParam.outBoundId,
            outDepartureLocation: flightAvailabilityParam.outDepartureLocation,
            outArrivalLocation: flightAvailabilityParam.outArrivalLocation,
            outDepartureDate: flightAvailabilityParam.outDepartureDate,
            outDepartureTimeFrom: flightAvailabilityParam.outDepartureTimeFrom,
            outDepartureTimeTo: flightAvailabilityParam.outDepartureTimeTo,
            outTransferAirport: flightAvailabilityParam.outTransferAirport,
            returnBoundId: flightAvailabilityParam.returnBoundId,
            returnDepartureLocation: flightAvailabilityParam.returnDepartureLocation,
            returnArrivalLocation: flightAvailabilityParam.returnArrivalLocation,
            returnDepartureDate: flightAvailabilityParam.returnDepartureDate,
            returnDepartureTimeFrom: flightAvailabilityParam.returnDepartureTimeFrom,
            returnDepartureTimeTo: flightAvailabilityParam.returnDepartureTimeTo,
            returnTransferAirport: flightAvailabilityParam.returnTransferAirport,
            cabinClass: flightAvailabilityParam.cabinClass,
            fareOptionType: flightAvailabilityParam.fareOptionType,
          });
        }
        // チェックイン検索共通処理を行う
        return await this.runCommonProcess(param, false, true);
      }
    } else {
      this.updateSearchFormParamForUpdate(this.checkinSearchService.getPostParameter());
    }

    return true;
  }

  /**
   * 検索条件をチェックイン検索共通処理に流し、遷移先を返す。
   * @param condition チェックイン検索APIのリクエストパラメータ
   * @param isTransitionOnError チェックイン検索APIでエラーが発生した際にチェックイン検索画面に遷移させるかどうか
   * @param isNotRetryableErrorOfCheckinSearch チェックイン検索APIでエラーになった場合に継続不可能エラーにするかどうか
   * @returns
   */
  async runCommonProcess(
    condition: PostJourneysSearchJourneyRequest,
    isTransitionOnError: boolean,
    isNotRetryableErrorOfCheckinSearch: boolean = false
  ): Promise<boolean | UrlTree> {
    const processResult = await lastValueFrom(
      this.checkinSearchService.checkinSearchCommonProcessOnGuard$(condition, isNotRetryableErrorOfCheckinSearch)
    );
    // booleanが帰ってくる場合にはエラーが発生しているので
    // 呼び出し元から指定された、チェックイン検索画面に流入しても良いかのフラグを返す
    if (typeof processResult === 'boolean') {
      return isTransitionOnError;
    }
    // Observableが返却された場合には、CPR情報取得APIの結果によってチェックイン対象選択に進むかチェックイン状況詳細に進むか決まる
    // もしURLが存在しなかった場合にはチェックイン検索画面に進む
    if (processResult instanceof Observable) {
      const url = await firstValueFrom(processResult);
      return !!url ? this.router.parseUrl(url) : true;
    } else {
      // それ以外の場合には、AbleDに遷移するためのURL
      if (!!processResult) {
        (window as any).location.href = processResult;
        return false;
      }
      return true;
    }
  }

  /**
   * 検索状態の取得
   */
  getSearchFormParamForUpdate$() {
    return this.searchFormParamForUpdate$.asObservable();
  }

  /**
   * @param 新しい検索パラメータの値
   * 検索状態の更新
   */
  updateSearchFormParamForUpdate(param: PostJourneysSearchJourneyRequest | undefined) {
    this.searchFormParamForUpdate$.next(param);
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    routerState: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // canActiveが呼ばれるたびにチェックイン検索に関連するstoreを初期化する
    this.checkinSearchService.clearCheckinSearchStore();
    return this.checkinSearchGuard();
  }
}
